import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { all_routes } from "../../../router/backendComponents/admin/all_routes";
import { useDispatch, useSelector } from "react-redux";
import {
  setExpandMenu,
  setMiniSidebar,
  setMobileSidebar,
} from "../../../core/data/redux/commonSlice";
import { HorizontalSidebarData } from "../../../core/data/json/horizontalSidebar";
import apiRequestCall from "../../../apiHelper/globalHelper";
import { fetchProfile } from "../../../core/data/redux/profileDetails";
import { fetchRolesPermissionsOverview } from "../../../core/data/redux/rolesPermissionsOverview";
import type { RootState, AppDispatch } from "../../../core/data/redux/store";

type Notif = {
  id: string | number;
  data: any;
  read_at?: string | null;
  created_at?: string;
};

interface Permission {
  id: number;
  role_id: number;
  name: string;
  role: {
    permissions: Record<string, Record<string, string>>;
  };
}

const Header: React.FC = () => {
  const [subOpen, setSubopen] = useState<string>("");
  const [subsidebar, setSubsidebar] = useState<string>("");
  const navigate = useNavigate();
  const [isFullscreen, setIsFullscreen] = useState(false);
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const token = useSelector((s: RootState) => s.profile?.data?.token);
  const profile = useSelector((s: RootState) => s.profile?.data);

  const mobileSidebar = useSelector(
    (state: any) => state.commonSlice.mobileSidebar
  );
  const miniSidebar = useSelector(
    (state: any) => state.commonSlice.miniSidebar
  );

  // header toggles
  const toggleMobileSidebar = () => dispatch(setMobileSidebar(!mobileSidebar));
  const toggleMiniSidebar = () => dispatch(setMiniSidebar(!miniSidebar));
  const toggleExpandMenu = () => dispatch(setExpandMenu(true));
  const toggleExpandMenu2 = () => dispatch(setExpandMenu(false));

  const permission = useSelector(
    (state: RootState) => state.rolesPermissions?.data
  ) as Permission | null;

  const permissionMap = useMemo(() => {
    return permission?.role?.permissions ?? {};
  }, [permission]);

  useEffect(() => {
    if (localStorage.getItem("authToken")) {
      dispatch(fetchProfile());
      dispatch(fetchRolesPermissionsOverview());
    }
  }, [dispatch]);

  interface Profile {
    profile_image?: File | string | null;
  }

  const backendRoute = all_routes.backend;
  const [layoutBs, setLayoutBs] = useState<string | null>(
    localStorage.getItem("dataTheme")
  );
  const isLockScreen = location.pathname === "/lock-screen";

  // previewSrc: memoize object URL when profile.profile_image is File, and cleanup on change/unmount
  const previewSrc = useMemo(() => {
    if (!profile?.profile_image) return undefined;
    if (profile.profile_image instanceof File) {
      return URL.createObjectURL(profile.profile_image);
    }
    return String(profile.profile_image);
  }, [profile?.profile_image]);

  useEffect(() => {
    // cleanup created object URL if profile_image is File
    return () => {
      if (profile?.profile_image instanceof File && previewSrc) {
        try {
          URL.revokeObjectURL(previewSrc);
        } catch {
          /* ignore */
        }
      }
    };
  }, [profile?.profile_image, previewSrc]);

  const LayoutDark = () => {
    localStorage.setItem("dataTheme", "dark");
    document.documentElement.setAttribute("data-theme", "dark");
    setLayoutBs("dark");
  };
  const LayoutLight = () => {
    localStorage.setItem("dataTheme", "light");
    document.documentElement.setAttribute("data-theme", "light");
    setLayoutBs("light");
  };

  const toggleSidebar = (title: any) => {
    localStorage.setItem("menuOpened", title);
    setSubopen((prev) => (prev === title ? "" : title));
  };

  const toggleSubsidebar = (subitem: any) => {
    setSubsidebar((prev) => (prev === subitem ? "" : subitem));
  };

  const toggleFullscreen = () => {
    if (!isFullscreen) {
      if (document.documentElement.requestFullscreen) {
        document.documentElement.requestFullscreen().catch(() => {});
        setIsFullscreen(true);
      }
    } else {
      if (document.exitFullscreen) {
        if (document.fullscreenElement) {
          document.exitFullscreen().catch(() => {});
        }
        setIsFullscreen(false);
      }
    }
  };

  const logOut = async () => {
    try {
      const result = await apiRequestCall("post", backendRoute["logout"]);
      if (result?.data?.code === 200) {
        localStorage.removeItem("authToken");
        navigate("/login");
      } else {
        alert("Failed to logout");
      }
    } catch (err) {
      console.error("Logout error:", err);
      alert("Failed to logout");
    }
  };

  // ----------------------------
  // Notifications
  // ----------------------------
  const [notifications, setNotifications] = useState<Notif[]>([]);
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const lastSeenIdRef = useRef<string | number | null>(null);
  const pollIntervalRef = useRef<number | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const isDark = layoutBs === "dark";
  const [isMobile, setIsMobile] = useState<boolean>(
    () => window.innerWidth <= 768
  );

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const fetchInitial = async () => {
    if (!token) return;
    try {
      const res = await apiRequestCall("post", backendRoute["notifications"], {
        data: { per_page: 20 },
      });

      if (!res || !res.data) {
        setNotifications([]);
        setUnreadCount(0);
        return;
      }

      // support both res.data and res.data.data shapes
      const list: any[] = Array.isArray(res.data)
        ? res.data
        : Array.isArray(res.data.data)
        ? res.data.data
        : [];
      const unread =
        typeof res.data.unread_count === "number" ? res.data.unread_count : 0;

      setNotifications(list);
      setUnreadCount(unread);

      if (list.length) {
        const ids = list.map((n: any) => n.id).filter(Boolean);
        // sort numeric/string-safe
        lastSeenIdRef.current =
          ids
            .sort((a: any, b: any) => (String(a) > String(b) ? 1 : -1))
            .slice(-1)[0] ?? null;
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("fetch notifications failed", err);
      setNotifications([]);
      setUnreadCount(0);
    }
  };

  const markRead = async (id: string | number) => {
    // optimistic
    setNotifications((prev) =>
      prev.map((n) =>
        String(n.id) === String(id)
          ? { ...n, read_at: new Date().toISOString() }
          : n
      )
    );
    setUnreadCount((c) => Math.max(0, c - 1));
    try {
      await apiRequestCall("post", backendRoute["markRead"], { data: { id } });
    } catch (err) {
      console.error("markRead failed", err);
      fetchInitial();
    }
  };

  const markAll = async () => {
    setNotifications((prev) =>
      prev.map((n) => ({ ...n, read_at: new Date().toISOString() }))
    );
    setUnreadCount(0);
    try {
      await apiRequestCall("post", backendRoute["markAllRead"]);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("markAll failed", err);
      fetchInitial();
    }
  };

  useEffect(() => {
    if (!token) return;

    fetchInitial();
    // poll every 3 seconds
    pollIntervalRef.current = window.setInterval(fetchInitial, 3000);

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  if (isLockScreen) return null;

  return (
    <>
      <div className="header">
        {/* Logo + toggles */}
        <div
          className="header-left active"
          onMouseEnter={toggleExpandMenu}
          onMouseLeave={toggleExpandMenu2}
        >
          <Link to="/" className="logo logo-normal" aria-label="Home">
            <img
              src="/assets/img/logo.png"
              alt="Logo"
              style={{ maxHeight: 40, width: "auto", objectFit: "contain" }}
            />
            <img
              src="/assets/img/logo.png"
              className="white-logo"
              alt="Logo"
              style={{ maxHeight: 40, width: "auto", objectFit: "contain" }}
            />
          </Link>

          <Link to="/" className="logo-small" aria-label="Home small">
            <img
              src="/assets/img/logo.png"
              alt="Logo"
              style={{ maxHeight: 32, width: "auto", objectFit: "contain" }}
            />
          </Link>

          <Link
            id="toggle_btn"
            to="#"
            onClick={toggleMiniSidebar}
            aria-label="Toggle sidebar"
          >
            <i className="ti ti-arrow-bar-to-left" />
          </Link>

          {/* Mobile-only small toggles */}
          {isMobile && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                gap: 16,
                marginRight: 20,
              }}
            >
              <button
                type="button"
                className="btn-ghost"
                aria-label="Light mode"
                onClick={LayoutLight}
                style={{
                  background: !isDark ? "rgba(243,156,18,0.12)" : "transparent",
                  borderRadius: 999,
                  padding: 6,
                  border: "none",
                }}
              >
                <i
                  className="ti ti-sun"
                  style={{ color: isDark ? "#999" : "#f39c12", fontSize: 18 }}
                />
              </button>

              <button
                type="button"
                className="btn-ghost"
                aria-label="Dark mode"
                onClick={LayoutDark}
                style={{
                  background: isDark ? "rgba(52,152,219,0.12)" : "transparent",
                  borderRadius: 999,
                  padding: 6,
                  border: "none",
                }}
              >
                <i
                  className="ti ti-moon"
                  style={{ color: isDark ? "#3498db" : "#999", fontSize: 18 }}
                />
              </button>

              {/* Notification bell: clicking navigates to /notification-push */}
              <Link
                to="/push-notification"
                aria-label={`Notifications (${unreadCount} unread)`}
                title="Notifications"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  position: "relative",
                  width: 38,
                  height: 38,
                  borderRadius: 10,
                  background: isDark ? "rgba(255,255,255,0.03)" : "transparent",
                  color: isDark ? "aquamarine" : "#fff",
                  textDecoration: "none",
                }}
              >
                <i
                  className="ti ti-bell"
                  style={{ fontSize: 18 }}
                  aria-hidden
                />
                {unreadCount > 0 && (
                  <span
                    className="badge rounded-pill"
                    style={{
                      position: "absolute",
                      top: -4,
                      right: -8,
                      fontSize: 10,
                      padding: "2px 6px",
                      background: "#ef4444",
                      color: "#fff",
                      borderRadius: 999,
                    }}
                  >
                    {unreadCount > 99 ? "99+" : unreadCount}
                  </span>
                )}
              </Link>
            </div>
          )}
        </div>

        {/* /Logo */}

        {/* Mobile menu button */}
        <Link
          id="mobile_btn"
          className="mobile_btn"
          to="#sidebar"
          onClick={toggleMobileSidebar}
        >
          <span className="bar-icon">
            <span />
            <span />
            <span />
          </span>
        </Link>

        {/* header user area */}
        <div className="header-user">
          <ul className="nav user-menu">
            <li className="nav-item nav-search-inputs me-auto" />

            {/* Horizontal Sidebar */}
            <li>
              <div
                className="sidebar sidebar-horizontal"
                id="horizontal-single"
              >
                <div className="sidebar-menu">
                  <div className="main-menu">
                    <ul className="nav-menu">
                      <li className="menu-title">
                        <span>Main</span>
                      </li>
                      {HorizontalSidebarData.length &&
                        HorizontalSidebarData.map((mainMenu, index) => (
                          <React.Fragment key={`main-${index}`}>
                            {mainMenu.menu.map((data, i) => (
                              <li className="submenu" key={`menu-${i}`}>
                                <Link
                                  to="#"
                                  className={`${
                                    data?.subMenus
                                      ?.map((link: any) => link?.route)
                                      .includes(location.pathname)
                                      ? "active"
                                      : ""
                                  } ${
                                    subOpen === data.menuValue ? "subdrop" : ""
                                  }`}
                                  onClick={() => toggleSidebar(data.menuValue)}
                                >
                                  <i className={`ti ti-${data.icon}`} />
                                  <span>{data.menuValue}</span>
                                  <span className="menu-arrow" />
                                </Link>

                                <ul
                                  style={{
                                    display:
                                      subOpen === data.menuValue
                                        ? "block"
                                        : "none",
                                  }}
                                >
                                  {data.subMenus?.map((subMenu: any, j) => (
                                    <li
                                      key={`submenu-${j}`}
                                      className={
                                        subMenu?.customSubmenuTwo
                                          ? "submenu"
                                          : ""
                                      }
                                    >
                                      <Link
                                        to={subMenu?.route || "#"}
                                        className={`${
                                          subMenu?.subMenusTwo
                                            ?.map((link: any) => link?.route)
                                            .includes(location.pathname) ||
                                          subMenu?.route === location.pathname
                                            ? "active"
                                            : ""
                                        } ${
                                          subsidebar === subMenu.menuValue
                                            ? "subdrop"
                                            : ""
                                        }`}
                                        onClick={() =>
                                          toggleSubsidebar(subMenu.menuValue)
                                        }
                                      >
                                        <span>{subMenu?.menuValue}</span>
                                        {subMenu?.customSubmenuTwo && (
                                          <span className="menu-arrow" />
                                        )}
                                      </Link>

                                      {subMenu?.customSubmenuTwo &&
                                        subMenu?.subMenusTwo && (
                                          <ul
                                            style={{
                                              display:
                                                subsidebar === subMenu.menuValue
                                                  ? "block"
                                                  : "none",
                                            }}
                                          >
                                            {subMenu.subMenusTwo.map(
                                              (subMenuTwo: any, k: number) => (
                                                <li key={`submenu-two-${k}`}>
                                                  <Link
                                                    className={
                                                      subMenuTwo.route ===
                                                      location.pathname
                                                        ? "active"
                                                        : ""
                                                    }
                                                    to={subMenuTwo.route}
                                                  >
                                                    {subMenuTwo.menuValue}
                                                  </Link>
                                                </li>
                                              )
                                            )}
                                          </ul>
                                        )}
                                    </li>
                                  ))}
                                </ul>
                              </li>
                            ))}
                          </React.Fragment>
                        ))}
                    </ul>
                  </div>
                </div>
              </div>
            </li>

            {/* Nav List (fullscreen, theme toggles, quick-add) */}
            <li className="nav-item nav-list">
              <ul className="nav">
                <li>
                  <Link
                    to="#"
                    onClick={(e) => {
                      e.preventDefault();
                      toggleFullscreen();
                    }}
                    className="btn btn-icon border btn-menubar btnFullscreen"
                    aria-label="Toggle fullscreen"
                  >
                    <i className="ti ti-maximize" />
                  </Link>
                </li>

                <li className="dark-mode-list">
                  <Link
                    to="#"
                    className={`dark-mode-toggle ${layoutBs ? "" : "active"}`}
                    id="dark-mode-toggle"
                    aria-label="Toggle theme"
                  >
                    <i
                      className={`ti ti-sun light-mode ${
                        layoutBs === "dark" ? "" : "active"
                      }`}
                      onClick={LayoutLight}
                    />
                    <i
                      className={`ti ti-moon dark-mode ${
                        layoutBs === "dark" ? "active" : ""
                      }`}
                      onClick={LayoutDark}
                    />
                  </Link>
                </li>

                <li className="nav-item dropdown">
                  <Link
                    to="#"
                    className="btn btn-header-list"
                    data-bs-toggle="dropdown"
                  >
                    <i className="ti ti-layout-grid-add" />
                  </Link>
                  <div className="dropdown-menu dropdown-menu-end menus-info">
                    <div className="row">
                      <div className="col-md-6">
                        <ul className="menu-list">
                          {(permissionMap?.source?.view_source ||
                            permissionMap?.source?.edit_source) && (
                            <li>
                              <Link to={"/source"}>
                                <div className="menu-details">
                                  <span className="menu-list-icon bg-violet">
                                    <i className="ti ti-database" />
                                  </span>
                                  <div className="menu-details-content">
                                    <p>Source</p>
                                    <span>Add New Souce</span>
                                  </div>
                                </div>
                              </Link>
                            </li>
                          )}

                          {(permissionMap?.principal?.view_principal ||
                            permissionMap?.principal?.edit_principal) && (
                            <li>
                              <Link to={"/principal"}>
                                <div className="menu-details">
                                  <span className="menu-list-icon bg-green">
                                    <i className="ti ti-building-factory" />
                                  </span>
                                  <div className="menu-details-content">
                                    <p>Principal</p>
                                    <span>Add New Principal</span>
                                  </div>
                                </div>
                              </Link>
                            </li>
                          )}

                          {(permissionMap?.courier?.view_courier ||
                            permissionMap?.courier?.edit_courier) && (
                            <li>
                              <Link to={"/courier"}>
                                <div className="menu-details">
                                  <span className="menu-list-icon bg-pink">
                                    <i className="ti ti-truck-delivery" />
                                  </span>
                                  <div className="menu-details-content">
                                    <p>Courier</p>
                                    <span>Add New Courier</span>
                                  </div>
                                </div>
                              </Link>
                            </li>
                          )}

                          {(permissionMap?.user?.view_user ||
                            permissionMap?.user?.edit_user) && (
                            <li>
                              <Link to={"/user"}>
                                <div className="menu-details">
                                  <span className="menu-list-icon bg-info">
                                    <i className="ti ti-users" />
                                  </span>
                                  <div className="menu-details-content">
                                    <p>User</p>
                                    <span>Shows All Users</span>
                                  </div>
                                </div>
                              </Link>
                            </li>
                          )}

                          {(permissionMap?.brand?.view_brand ||
                            permissionMap?.brand?.edit_brand) && (
                            <li>
                              <Link to={"/brand"}>
                                <div className="menu-details">
                                  <span className="menu-list-icon bg-danger">
                                    <i className="ti ti-tag" />
                                  </span>
                                  <div className="menu-details-content">
                                    <p>Brand</p>
                                    <span>Add New Brand</span>
                                  </div>
                                </div>
                              </Link>
                            </li>
                          )}
                        </ul>
                      </div>
                      <div className="col-md-6">
                        <ul className="menu-list">
                          {(permissionMap?.notification?.view_notification ||
                            permissionMap?.notification?.edit_notification) && (
                            <li>
                              <Link to={"/notification"}>
                                <div className="menu-details">
                                  <span className="menu-list-icon bg-info">
                                    <i className="ti ti-notification" />
                                  </span>
                                  <div className="menu-details-content">
                                    <p>Notification</p>
                                    <span>Add New Notification</span>
                                  </div>
                                </div>
                              </Link>
                            </li>
                          )}

                          {(permissionMap?.usp?.view_usp ||
                            permissionMap?.usp?.edit_usp) && (
                            <li>
                              <Link to={"/usp"}>
                                <div className="menu-details">
                                  <span className="menu-list-icon bg-secondary">
                                    <i className="ti ti-chart-arcs" />
                                  </span>
                                  <div className="menu-details-content">
                                    <p>USP</p>
                                    <span>Add New USP</span>
                                  </div>
                                </div>
                              </Link>
                            </li>
                          )}

                          {(permissionMap?.customer?.view_customer ||
                            permissionMap?.customer?.edit_customer) && (
                            <li>
                              <Link to={"/customer"}>
                                <div className="menu-details">
                                  <span className="menu-list-icon bg-tertiary">
                                    <i className="ti ti-users" />
                                  </span>
                                  <div className="menu-details-content">
                                    <p>Customer</p>
                                    <span>Add New Customer</span>
                                  </div>
                                </div>
                              </Link>
                            </li>
                          )}

                          {(permissionMap?.reason?.view_reason ||
                            permissionMap?.reason?.edit_reason) && (
                            <li>
                              <Link to={"/reason"}>
                                <div className="menu-details">
                                  <span className="menu-list-icon bg-success">
                                    <i className="ti ti-list-check" />
                                  </span>
                                  <div className="menu-details-content">
                                    <p>Reason</p>
                                    <span>Add New Reason</span>
                                  </div>
                                </div>
                              </Link>
                            </li>
                          )}

                          {(permissionMap?.order?.view_order ||
                            permissionMap?.order?.edit_order) && (
                            <li>
                              <Link to={"/order"}>
                                <div className="menu-details">
                                  <span className="menu-list-icon bg-purple">
                                    <i className="ti ti-shopping-cart" />
                                  </span>
                                  <div className="menu-details-content">
                                    <p>Order</p>
                                    <span>Add New Order</span>
                                  </div>
                                </div>
                              </Link>
                            </li>
                          )}
                          {(permissionMap?.product?.view_product ||
                            permissionMap?.product?.edit_product) && (
                            <li>
                              <Link to={"/product"}>
                                <div className="menu-details">
                                  <span className="menu-list-icon bg-yellow">
                                    <i className="ti ti-box" />
                                  </span>
                                  <div className="menu-details-content">
                                    <p>Product</p>
                                    <span>View Product</span>
                                  </div>
                                </div>
                              </Link>
                            </li>
                          )}
                        </ul>
                      </div>
                    </div>
                  </div>
                </li>
              </ul>
            </li>

            <li className="nav-item dropdown nav-item-box">
              <Link to="#" className="nav-link" data-bs-toggle="dropdown">
                <i className="ti ti-bell" />
                {unreadCount > 0 && (
                  <span className="badge rounded-pill">
                    {unreadCount > 99 ? "99+" : unreadCount}
                  </span>
                )}
              </Link>

              <div className="dropdown-menu dropdown-menu-end notification-dropdown">
                <div className="topnav-dropdown-header d-flex justify-content-between align-items-center">
                  <h4 className="notification-title mb-0">Notifications</h4>
                  {notifications.length !== 0 && (
                    <button className="btn btn-sm btn-link" onClick={markAll}>
                      Mark all read
                    </button>
                  )}
                </div>

                <div
                  className="noti-content"
                  style={{ maxHeight: 360, overflowY: "auto" }}
                >
                  <ul className="notification-list">
                    {notifications.length === 0 && (
                      <li className="notification-message text-center text-muted py-3">
                        No notifications
                      </li>
                    )}

                    {notifications.map((n) => {
                      const meta = n.data?.meta ?? {};
                      const creator = meta.created_by ?? "System";
                      const branch = meta.branch ?? "-";
                      const type = meta.type ?? "-";
                      const amount = meta.amount
                        ? Number(meta.amount).toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })
                        : "-";
                      const quotationNo = meta.quotation_no ?? "";
                      const orderNo = meta.order_no ?? "";
                      const message = meta.message ?? n.data?.message ?? "";
                      const when = meta.created_at ?? n.created_at;
                      const partialOrderNo = meta.partial_order ?? "";
                      const status =
                        orderNo !== "" && meta?.status === "open"
                          ? "win"
                          : quotationNo !== "" && meta.status == null
                          ? "open"
                          : meta.status ?? "-";

                      return (
                        <li key={String(n.id)} className="notification-message">
                          <div
                            role="button"
                            tabIndex={0}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              if (expandedId !== String(n.id) && !n.read_at)
                                markRead(n.id);
                              setExpandedId((prev) =>
                                prev === String(n.id) ? null : String(n.id)
                              );
                            }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                e.stopPropagation();
                                if (expandedId !== String(n.id) && !n.read_at)
                                  markRead(n.id);
                                setExpandedId((prev) =>
                                  prev === String(n.id) ? null : String(n.id)
                                );
                              }
                            }}
                            style={{ cursor: "pointer", padding: 12 }}
                          >
                            <div
                              className="media d-flex"
                              style={{ gap: 12, alignItems: "flex-start" }}
                            >
                              <span
                                className="avatar flex-shrink-0"
                                style={{ position: "relative" }}
                              >
                                <img
                                  src={previewSrc}
                                  alt="Profile"
                                  style={{
                                    width: 40,
                                    height: 40,
                                    borderRadius: 6,
                                  }}
                                />
                                {!n.read_at && (
                                  <span
                                    className="badge rounded-pill"
                                    style={{
                                      position: "absolute",
                                      right: -4,
                                      top: -4,
                                      width: 10,
                                      height: 10,
                                      borderRadius: "50%",
                                      background: "#0d6efd",
                                      display: "inline-block",
                                    }}
                                  />
                                )}
                              </span>

                              <div
                                className="media-body flex-grow-1"
                                style={{ minWidth: 0 }}
                              >
                                <p
                                  className="noti-details mb-1 d-flex justify-content-between align-items-center"
                                  style={{ margin: 0 }}
                                >
                                  <strong
                                    style={{
                                      whiteSpace: "nowrap",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                    }}
                                  >
                                    {creator}
                                  </strong>

                                  {!n.read_at && (
                                    <button
                                      type="button"
                                      className="badge badge-soft-success rounded-pill"
                                      style={{
                                        cursor: "pointer",
                                        border: "none",
                                        background: "#d1fae5",
                                      }}
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        markRead(n.id);
                                      }}
                                    >
                                      <small>Mark as read</small>
                                    </button>
                                  )}
                                </p>

                                <div style={{ marginTop: 8 }}>
                                  <p
                                    className="noti-sub-details mb-1"
                                    style={{ margin: 0 }}
                                  >
                                    <small
                                      style={{
                                        padding: "2px 8px",
                                        borderRadius: 8,
                                        fontWeight: 600,
                                        fontSize: 12,
                                        background: "#f1f1f1",
                                        color: "#333",
                                      }}
                                    >
                                      {message}
                                    </small>
                                  </p>

                                  <p
                                    className="noti-sub-details mb-1"
                                    style={{ margin: "6px 0" }}
                                  >
                                    {type === "quotation" ||
                                      (type === "quotation_status" && (
                                        <small>
                                          Quotation No. :{" "}
                                          <strong>{quotationNo}</strong>
                                        </small>
                                      ))}

                                    {type === "order" && (
                                      <small>
                                        Order No. : <strong>{orderNo}</strong>
                                      </small>
                                    )}

                                    {type === "partial_order" && (
                                      <small>
                                        Partial Order No. :{" "}
                                        <strong>{partialOrderNo}</strong>
                                      </small>
                                    )}
                                  </p>

                                  <p
                                    className="noti-sub-details mb-1"
                                    style={{ margin: "6px 0" }}
                                  >
                                    <small>
                                      Amount : <strong>{amount}</strong>
                                    </small>
                                  </p>

                                  <p
                                    className="noti-sub-details mb-1"
                                    style={{ margin: "6px 0" }}
                                  >
                                    <small>
                                      Branch : <strong>{branch}</strong>
                                    </small>
                                  </p>

                                  <p
                                    className="noti-sub-details mb-1"
                                    style={{ margin: "6px 0" }}
                                  >
                                    <small>
                                      Status :{" "}
                                      <span
                                        style={{
                                          padding: "2px 8px",
                                          borderRadius: 8,
                                          fontWeight: 600,
                                          fontSize: 12,
                                          background: "#f1f1f1",
                                          color: "#333",
                                        }}
                                      >
                                        {status}
                                      </span>
                                    </small>
                                  </p>

                                  <p
                                    className="noti-time text-muted"
                                    style={{ marginTop: 6 }}
                                  >
                                    {when}
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>

                {notifications.length !== 0 && (
                  <div className="topnav-dropdown-footer d-flex justify-content-between">
                    <Link to={"/push-notification"} className="view-link">
                      View all
                    </Link>
                    <a
                      href="#"
                      className="clear-link"
                      onClick={(e) => {
                        e.preventDefault();
                        setNotifications([]);
                        setUnreadCount(0);
                      }}
                    >
                      Clear all
                    </a>
                  </div>
                )}
              </div>
            </li>

            {/* Profile dropdown */}
            <li className="nav-item dropdown has-arrow main-drop">
              <Link
                to="#"
                className="nav-link userset"
                data-bs-toggle="dropdown"
              >
                <span className="user-info">
                  <span className="user-letter">
                    <img
                      src={previewSrc ?? "assets/img/profile.png"}
                      alt="Profile"
                      style={{ width: 32, height: 32, borderRadius: 6 }}
                    />
                  </span>
                  <span className="badge badge-success rounded-pill" />
                </span>
              </Link>
              <div className="dropdown-menu menu-drop-user">
                <div className="profilename">
                  <Link className="dropdown-item" to={"/"}>
                    <i className="ti ti-layout-2" /> Dashboard
                  </Link>
                  <Link className="dropdown-item" to={"/profile-details"}>
                    <i className="ti ti-user-pin" /> My Profile
                  </Link>
                  <Link className="dropdown-item" to={""} onClick={logOut}>
                    <i className="ti ti-lock" /> Logout
                  </Link>
                </div>
              </div>
            </li>
          </ul>
        </div>

        {/* Mobile user menu (three dots) */}
        <div className="dropdown mobile-user-menu">
          <Link
            to="#"
            className="nav-link dropdown-toggle"
            data-bs-toggle="dropdown"
            aria-expanded="false"
          >
            <i className="fa fa-ellipsis-v" />
          </Link>
          <div className="dropdown-menu">
            <Link className="dropdown-item" to={""}>
              <i className="ti ti-layout-2" /> Dashboard
            </Link>
            <Link className="dropdown-item" to={"/profile-details"}>
              <i className="ti ti-user-pin" /> My Profile
            </Link>
            <Link className="dropdown-item" to={""} onClick={logOut}>
              <i className="ti ti-lock" /> Logout
            </Link>
          </div>
        </div>
      </div>
    </>
  );
};

export default Header;
